import { Box, Tooltip, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import NoDataCard from '../../../../../Dashboard/detailPanel/NoDataCard';
import CustomerCareDTable from '../../../../../Shared/CustomerCareDTable';
import StatusChip from '../../../../../Shared/StatusChip';
import {
  faCircleExclamation,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../../store';
import { AdherenceStatus } from '../../ScheduleTab/utils/statusUtils';
import { fetchOwnerOverview } from '../../../../../../store/hubOwner/actionCreators';
import LoadingSpinner from '../../../../../Shared/LoadingSpinner';
import {
  customError,
  dimGrey,
  lightBackground1,
  warningBg,
  warningYellow,
} from '../../../../../../mainTheme';

const getStatusConfig = (status: string) => {
  if (status === AdherenceStatus.MISSED) {
    return {
      icon: faTriangleExclamation,
      color: customError,
      text: 'Missed',
      backgroundColor: '#FCEFEE',
    };
  } else if (status === AdherenceStatus.PAST_DUE) {
    return {
      icon: faTriangleExclamation,
      color: customError,
      text: 'Past due',
      backgroundColor: '#FCEFEE',
    };
  }
  return {
    icon: faCircleExclamation,
    color: warningYellow,
    text: status || 'Unknown',
    backgroundColor: warningBg,
  };
};

const toTitleCase = (str: string) =>
  str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const AdherenceDetailsPanel = ({ customerId }: { customerId: number }) => {
  const dispatch = useDispatch();

  const { ownerOverview, ownerOverviewLoading } = useSelector(
    (state: RootState) => state.hubOwner,
  );

  useEffect(() => {
    if (!ownerOverview || ownerOverview.ownerId !== customerId) {
      dispatch(fetchOwnerOverview(customerId));
    }
  }, [dispatch, customerId, ownerOverview]);

  const rows =
    ownerOverview?.med_details?.map((item, idx) => ({
      id: idx,
      assist: item.event_type
        ? toTitleCase(item.event_type.replace('_', ' '))
        : 'Unknown',
      med_status: item.status,
      time: item.prescribed_datetime,
      dose_count: item.dose_count,
    })) ?? [];

  const columns = [
    {
      field: 'med_status',
      headerName: 'Med status',
      flex: 2,
      renderCell: (params: any) => {
        const config = getStatusConfig(params.row.med_status);
        return (
          <Tooltip
            title={
              <Box
                sx={{
                  p: 2,
                  bgcolor: lightBackground1,
                  border: '1px solid #D4D4D9',
                  borderRadius: '8px',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                  maxWidth: {
                    xs: '18.75rem',
                    sm: '37.5rem',
                    md: '53.125rem',
                    lg: '62.5rem',
                    xl: '78.125rem',
                  },
                  color: dimGrey,
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0.2px',
                  }}
                >
                  Dose count: {params.row.dose_count}
                </Typography>
              </Box>
            }
            placement="bottom-start"
            componentsProps={{
              tooltip: {
                sx: { bgcolor: 'transparent', p: 0, maxWidth: 'none' },
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StatusChip
                icon={config.icon}
                iconColor={config.color}
                labelText={config.text}
                backgroundColor={config.backgroundColor}
                conditionalValue={params.row.severity}
              />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'assist',
      headerName: 'Assistance type',
      flex: 2,
    },
    {
      field: 'time',
      headerName: 'Prescribed time',
      flex: 2,
      renderCell: (params: any) => <span>{params.row.time || 'N/A'}</span>,
    },
  ];

  if (ownerOverviewLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {rows.length ? (
        <Box p={2}>
          <CustomerCareDTable
            rows={rows}
            columns={columns}
            loading={ownerOverviewLoading}
            pageSize={5}
            defaultSortField="med_status"
            defaultSortDirection="desc"
            hideViewAllButton
          />
        </Box>
      ) : (
        <NoDataCard />
      )}
    </>
  );
};

export default AdherenceDetailsPanel;
